/**
 * Beats at the edges of what the IR can hold (W1-07a formats-json-rpe; DESIGN.md §7.2, D2).
 *
 * An IR beat is a normalised RPE triple: whole part in i32, denominator at most 2^32 - 1. Normalisation moves every
 * beat by the first BPM point's beat (normTempo.ts) and adds beats of its own (the 1-beat tail of a final jump, the
 * pad before a first event, the nodes of a baked curve). Near the i32 limits, or with a first BPM point at a
 * fraction with a large denominator, such a beat can leave what a triple holds. Phira itself loads those charts
 * (its beats are f64), so normalisation must never fail on them: it keeps its additions inside the range (a final
 * jump at the last whole beat gets a fraction of a beat instead of one beat) and refuses, with a report entry, the
 * source items whose shifted beat the IR cannot hold.
 *
 * Every helper here works on exact rationals and never throws.
 */

import { Rat } from '../../core/math/rational';
import type { BigRational } from '../../core/math/rational';
import { beatFromRat, beatFromRatExact, beatToRat, Beats, I32_MAX, I32_MIN } from '../../core/time/beat';
import { formatBeat } from '../../core/time/beatSyntax';
import type { Beat } from '../../core/time/types';

/**
 * `formatBeat(b)` (colon style, "-3:1/4") for report parameters. core/time's formatter negates negative beats
 * first, which leaves i32 for the very first beat of the range (-2^31): that one is written here directly.
 */
export function beatLabel(b: Beat): string {
  return b.w === I32_MIN && b.n === 0 ? `-${String(-I32_MIN)}:0/1` : formatBeat(b);
}

/**
 * `beat - shift` exactly (the output beat of a source beat); null when the IR cannot hold it: its whole part leaves
 * i32 or its reduced denominator exceeds 2^32 - 1. `shift` null = no shift.
 */
export function shiftedBeat(beat: Beat, shift: Beat | null): Beat | null {
  if (shift === null) return beat;
  if (shift.n === 0) {
    // Whole shifts keep the fraction: only the range can fail (the common case, no BigInt).
    const w = beat.w - shift.w;
    if (w < I32_MIN || w > I32_MAX) return null;
    return beat.n === 0 ? { w: w === 0 ? 0 : w, n: 0, d: 1 } : { w, n: beat.n, d: beat.d };
  }
  return beatFromRatExact(Rat.sub(beatToRat(beat), beatToRat(shift)));
}

/** True when the output beat of the source beat `beat` fits a triple exactly. */
export function fitsShifted(beat: Beat, shift: Beat | null): boolean {
  return shiftedBeat(beat, shift) !== null;
}

function ratOfParts(w: bigint, num: bigint, den: bigint): BigRational {
  return Rat.add(Rat.of(w), Rat.of(num, den));
}

/** A few values strictly between `b` and the next whole beat, with denominators close to b's own. */
function fractionsAfter(b: Beat): BigRational[] {
  const w = BigInt(b.w);
  const n = BigInt(b.n);
  const d = BigInt(b.d);
  const out = [
    ratOfParts(w, n + d, 2n * d), // halfway to the next whole beat
    ratOfParts(w, n + 1n, d + 1n), // mediant of n/d and 1/1
  ];
  if (n + 1n < d) out.push(ratOfParts(w, n + 1n, d)); // the next 1/d step
  return out;
}

/** A few values strictly between the previous whole beat (or `b - 1` for a whole `b`) and `b`. */
function fractionsBefore(b: Beat): BigRational[] {
  const w = BigInt(b.w);
  const n = BigInt(b.n);
  const d = BigInt(b.d);
  if (n === 0n) return [ratOfParts(w - 1n, 1n, 2n)];
  const out = [
    Rat.of(w), // the whole beat below
    ratOfParts(w, n, 2n * d), // halfway down to it
  ];
  if (n > 1n) out.push(ratOfParts(w, n - 1n, d)); // the previous 1/d step
  return out;
}

/**
 * Candidates around `beat` in source beats and, with a shift, in output beats (converted back), so that whichever
 * side is closer to its limit decides. Returns the first candidate that is a valid source beat on the right side
 * of `beat` whose output beat also fits.
 */
function search(
  beat: Beat,
  shift: Beat | null,
  step: BigRational,
  fractions: (b: Beat) => BigRational[],
  after: boolean,
): Beat | null {
  const accept = (src: BigRational): Beat | null => {
    const b = beatFromRatExact(src);
    if (b === null) return null;
    if (after ? !Beats.lt(beat, b) : !Beats.lt(b, beat)) return null;
    return fitsShifted(b, shift) ? b : null;
  };
  const x = beatToRat(beat);
  const whole = accept(Rat.add(x, step));
  if (whole !== null) return whole;
  for (const f of fractions(beat)) {
    const c = accept(f);
    if (c !== null) return c;
  }
  if (shift !== null) {
    const o = shiftedBeat(beat, shift);
    if (o !== null) {
      const s = beatToRat(shift);
      for (const f of fractions(o)) {
        const c = accept(Rat.add(f, s));
        if (c !== null) return c;
      }
    }
  }
  return null;
}

/**
 * A source beat after `beat` whose output beat fits: `beat + 1` when possible, else a fraction of a beat later
 * (at the end of the beat range). null when no later beat exists (e.g. `beat` is the very last one).
 */
export function beatAfter(beat: Beat, shift: Beat | null): Beat | null {
  return search(beat, shift, Rat.ONE, fractionsAfter, true);
}

/**
 * A source beat before `beat` whose output beat fits: `beat - 1` when possible, else a fraction of a beat earlier
 * (at the start of the beat range). null when no earlier beat exists.
 */
export function beatBefore(beat: Beat, shift: Beat | null): Beat | null {
  return search(beat, shift, Rat.neg(Rat.ONE), fractionsBefore, false);
}

/**
 * `a + (b - a) * j / n` (a < b, 0 <= j <= n) in source beats: exact rationals, so a span wider than i32 never
 * overflows. A denominator above 2^32 - 1 falls back to the nearest multiple of 1/80640 (Beats' computed-result
 * policy). null when that rounded beat leaves [a, b] or the beat range, or when its output beat does not fit (only
 * possible with a fractional shift): the caller then simply has one node fewer.
 */
export function beatBetween(a: Beat, b: Beat, j: number, n: number, shift: Beat | null): Beat | null {
  const ra = beatToRat(a);
  const r = Rat.add(ra, Rat.mul(Rat.sub(beatToRat(b), ra), Rat.of(j, n)));
  let beat = beatFromRatExact(r);
  if (beat === null) {
    try {
      beat = beatFromRat(r);
    } catch {
      // Rounded past the last whole beat of the range.
      return null;
    }
  }
  if (Beats.lt(beat, a) || Beats.lt(b, beat)) return null;
  return fitsShifted(beat, shift) ? beat : null;
}
